<template>
  <div
    class="messenger-widget-minimized-view"
    :class="`messenger-widget-minimized-view--align-${sharedSettings.placement.alignTo}`"
  >
    <transition name="slide-fade">
      <div
        v-if="viewSettings.eyeCatcherText && isTooltipVisible"
        class="messenger-widget-minimized-view__tooltip messenger-tooltip"
      >
        <messenger-text-with-emoji
          :text="viewSettings.eyeCatcherText"
          emoji-size="14px"
          class="messenger-tooltip__text"
        />
        <widget-icon
          name="close"
          class="messenger-tooltip__close"
          @click="$emit('close-tooltip')"
        />
      </div>
    </transition>

    <messenger-start-button
      ref="startButtonRef"
      :is-expanded="false"
      :text="viewSettings.buttonText"
      :border-radius="viewSettings.borderRadius"
      :icon-name="viewSettings.icon"
      :has-notification="hasNotification"
      @toggle-expand="$emit('expand')"
    />
  </div>
</template>

<script setup lang="ts">
import MessengerStartButton from '@widgets/messenger/components/MessengerStartButton.vue'
import type { MessengerMinimizedViewSettings } from '@widgets/messenger/types'
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import { useWidgetSharedSettings } from '@widgets/messenger/compositions/useWidgetSharedSettings'
import MessengerTextWithEmoji from '@widgets/messenger/components/MessengerTextWithEmoji.vue'
import { useHtmlElementFromTemplateRef } from '@/composition/useHtmlElementFromTemplateRef'
import { type ComponentPublicInstance, ref } from '@/composition/vue/compositionApi'
import { useResizeObserver } from '@/composition/resizeObserver'

withDefaults(
  defineProps<{
    viewSettings: MessengerMinimizedViewSettings
    isTooltipVisible?: boolean
    hasNotification?: boolean
  }>(),
  {
    isTooltipVisible: true,
    hasNotification: false,
  },
)

const emit = defineEmits<{
  (e: 'expand'): void
  (e: 'close-tooltip'): void
  (e: 'start-button-resize', value: { width: number; height: number })
}>()

const sharedSettings = useWidgetSharedSettings()

const startButtonRef = ref<ComponentPublicInstance | null>(null)
const startButtonElementRef = useHtmlElementFromTemplateRef(startButtonRef)

useResizeObserver(startButtonElementRef, (entries) => {
  const target = entries[0].target as HTMLElement
  emit('start-button-resize', {
    width: target.offsetWidth,
    height: target.offsetHeight,
  })
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';

.messenger-widget-minimized-view {
  display: inline-flex;
  flex-direction: column;
  gap: 10px;

  &--align-left {
    align-items: flex-start;
  }

  &--align-right {
    align-items: flex-end;
  }
}

.messenger-tooltip {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: $border-radius-md;
  border: 1px solid $gray2;
  background: $white;
  box-shadow: 0 4px 24px 0 rgba($gray10, 0.13);
  max-width: 300px;
  word-break: break-word;

  padding: 10px 16px;
  color: $emphasize;
  font-weight: $medium-font;

  &__close {
    color: $distinct;
    cursor: pointer;
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition:
    transform $transition-300ms-ease-in-out,
    opacity $transition-300ms-ease-in-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
